import { useMemo } from 'react';

import type { LogContext } from '@/services/logger';
import { createLogger } from '@/services/logger';

// Types for the hook
export type UseLoggerOptions = {
	component?: string;
	prefix?: string;
	defaultContext?: LogContext;
};

export type UseLoggerReturn = {
	debug: (message: string, data?: unknown, context?: LogContext) => void;
	info: (message: string, data?: unknown, context?: LogContext) => void;
	warn: (message: string, data?: unknown, context?: LogContext) => void;
	error: (message: string, error?: unknown, context?: LogContext) => void;
	time: <T>(label: string, fn: () => T | Promise<T>) => Promise<T>;
	group: (label: string, fn: () => void) => void;
	assert: (condition: boolean, message: string, data?: unknown) => void;
	table: (data: unknown, columns?: string[]) => void;
};

/**
 * React hook for accessing the logger service with component context
 *
 * @param options - Configuration options for the logger
 * @returns Logger instance with component context
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const logger = useLogger({ component: 'MyComponent' });
 *
 *   const handleClick = () => {
 *     logger.info('Button clicked', { buttonId: 'submit' });
 *   };
 *
 *   return <button onClick={handleClick}>Click me</button>;
 * };
 * ```
 */
export const useLogger = (options: UseLoggerOptions = {}): UseLoggerReturn => {
	const logger = useMemo(() => {
		const { component, prefix, defaultContext } = options;

		const loggerInstance = createLogger({ prefix });

		const wrapLogMethod =
			(method: 'debug' | 'info' | 'warn' | 'error') =>
			(message: string, data?: unknown, context?: LogContext) => {
				const mergedContext: LogContext = {
					...defaultContext,
					...context,
					component: context?.component || defaultContext?.component || component,
				};
				loggerInstance[method](message, data, mergedContext);
			};

		return {
			debug: wrapLogMethod('debug'),
			info: wrapLogMethod('info'),
			warn: wrapLogMethod('warn'),
			error: wrapLogMethod('error'),
			time: loggerInstance.time.bind(loggerInstance),
			group: loggerInstance.group.bind(loggerInstance),
			assert: loggerInstance.assert.bind(loggerInstance),
			table: loggerInstance.table.bind(loggerInstance),
		};
	}, [options.component, options.prefix, options.defaultContext]);

	return logger;
};
